import type {
  OperationalTakeoverPreparation,
  OperationalTakeoverState,
} from "./operationalTakeoverTypes";

import type { OperationalTakeoverStatus } from "./operationalTakeoverStatus";

export interface CyanTakeoverPhaseView {
  readonly id: string;
  readonly title: string;
  readonly status: "pending" | "active" | "success" | "error";

  readonly completed: number;
  readonly total: number;
}

export interface CyanTakeoverChecklistView {
  readonly id: string;
  readonly title: string;
  readonly department: string;
  readonly phaseId: string;

  readonly status: "pending" | "active" | "success" | "error";

  readonly evidence: readonly string[];
}

export interface CyanTakeoverQueueItemView {
  readonly id: string;
  readonly phaseId: string;
  readonly department: string;
  readonly objective: string;

  readonly status: "pending" | "active" | "success" | "error";
}

export interface CyanTakeoverLiveViewModel {
  readonly state: OperationalTakeoverState;

  readonly objective: string;

  readonly progressPercent: number;

  readonly phases: readonly CyanTakeoverPhaseView[];

  readonly checklist: readonly CyanTakeoverChecklistView[];

  readonly queue: readonly CyanTakeoverQueueItemView[];

  readonly approvalRequired: readonly string[];

  readonly blocked: readonly string[];
}

export function createTakeoverPreparationView(
  preparation: OperationalTakeoverPreparation,
): CyanTakeoverLiveViewModel {
  const grouped = new Map<
    string,
    {
      total: number;
      completed: number;
    }
  >();

  for (const work of preparation.workPackages) {
    const current = grouped.get(work.phaseId) ?? {
      total: 0,
      completed: 0,
    };

    grouped.set(work.phaseId, {
      ...current,
      total: current.total + 1,
    });
  }

  return {
    state: preparation.state,

    objective: preparation.objective,

    progressPercent: 0,

    phases: [...grouped.entries()].map(([id, value]) => ({
      id,
      title: id.replace(/^repair:/, ""),
      status: "pending",
      completed: value.completed,
      total: value.total,
    })),

    checklist: preparation.workPackages.map((work) => ({
      id: work.id,

      title: work.objective,

      department: work.department,

      phaseId: work.phaseId,

      status: "pending",

      evidence: [],
    })),

    queue: preparation.workPackages.map((work) => ({
      id: work.id,

      phaseId: work.phaseId,

      department: work.department,

      objective: work.objective,

      status: "pending",
    })),

    approvalRequired: preparation.approvalRequired,

    blocked: preparation.blocked,
  };
}

export function createTakeoverExecutionView(
  preparation: OperationalTakeoverPreparation,

  status: OperationalTakeoverStatus,
): CyanTakeoverLiveViewModel {
  const phaseMap = new Map<string, CyanTakeoverPhaseView>();

  for (const item of status.checklist) {
    const current = phaseMap.get(item.phaseId) ?? {
      id: item.phaseId,

      title: item.phaseId.replace(/^repair:/, ""),

      status: "pending",

      completed: 0,

      total: 0,
    };

    const total = current.total + 1;

    const completed = current.completed + (item.status === "success" ? 1 : 0);

    let phaseStatus: CyanTakeoverPhaseView["status"] = "pending";

    if (item.status === "error") {
      phaseStatus = "error";
    } else if (item.status === "active" || current.status === "active") {
      phaseStatus = "active";
    } else if (completed === total) {
      phaseStatus = "success";
    }

    phaseMap.set(item.phaseId, {
      ...current,
      status: phaseStatus,
      completed,
      total,
    });
  }

  return {
    state:
      status.state === "GREEN"
        ? "GREEN"
        : status.state === "BLOCKED" || status.failed > 0
          ? "BLOCKED"
          : "RUNNING",

    objective: status.objective,

    progressPercent: status.progressPercent,

    phases: [...phaseMap.values()],

    checklist: status.checklist.map((item) => ({
      id: item.id,

      title: item.title,

      department: item.department,

      phaseId: item.phaseId,

      status: item.status,

      evidence: item.evidence,
    })),

    queue: status.checklist.map((item) => ({
      id: item.id,

      phaseId: item.phaseId,

      department: item.department,

      objective: item.title,

      status: item.status,
    })),

    approvalRequired: preparation.approvalRequired,

    blocked: preparation.blocked,
  };
}
